import React, { useState, useEffect, useCallback } from 'react';
import chemicalService from '../../../services/chemicalService';
import VendorFormModal from '../components/VendorFormModal';

const PAGE_SIZE = 10;

const VendorsTab = () => {
  const [vendors, setVendors] = useState([]);
  const [summary, setSummary] = useState({ total: 0, raw_material: 0, trading_goods: 0, total_payables: 0 });
  const [pagination, setPagination] = useState({ page: 1, limit: PAGE_SIZE, total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);

  const [typeFilter, setTypeFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);

  const [showFormModal, setShowFormModal] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);

  // Debounce search input by 400ms
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [typeFilter, debouncedSearch]);

  const fetchVendors = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, limit: PAGE_SIZE };
      if (typeFilter !== 'all') params.type = typeFilter;
      if (debouncedSearch.trim()) params.search = debouncedSearch.trim();

      const [listRes, summaryRes] = await Promise.all([
        chemicalService.getVendors(params),
        chemicalService.getVendorsSummary(),
      ]);

      setVendors(listRes.data || []);
      setPagination(listRes.pagination || { page: 1, limit: PAGE_SIZE, total: 0, totalPages: 1 });
      setSummary(summaryRes.data || { total: 0, raw_material: 0, trading_goods: 0, total_payables: 0 });
    } catch (err) {
      console.error('Error fetching vendors:', err);
      alert('❌ ' + (err.response?.data?.message || 'Failed to load vendors'));
    } finally {
      setLoading(false);
    }
  }, [page, typeFilter, debouncedSearch]);

  useEffect(() => {
    fetchVendors();
  }, [fetchVendors]);

  const handleAddClick = () => {
    setEditingVendor(null);
    setShowFormModal(true);
  };

  const handleEditClick = (vendor) => {
    setEditingVendor(vendor);
    setShowFormModal(true);
  };

  const handleDelete = async (vendor) => {
    if (!window.confirm(`Delete vendor "${vendor.name}"?\n\nThis cannot be undone.`)) return;
    try {
      await chemicalService.deleteVendor(vendor.id);
      alert('✅ Vendor deleted successfully!');
      if (vendors.length === 1 && page > 1) {
        setPage(page - 1);
      } else {
        fetchVendors();
      }
    } catch (err) {
      alert('❌ ' + (err.response?.data?.message || 'Failed to delete vendor'));
    }
  };

  const handleSaved = () => {
    setShowFormModal(false);
    setEditingVendor(null);
    fetchVendors();
  };

  return (
    <div style={{ padding: '24px 30px 40px' }}>

      {/* ─── Summary Cards ───────────────────────────── */}
      <div style={styles.summaryGrid}>
        <div style={{ ...styles.summaryCard, borderLeft: '4px solid #2c3e50' }}>
          <div style={styles.summaryLabel}>Total Vendors</div>
          <div style={{ ...styles.summaryValue, color: '#2c3e50' }}>{summary.total}</div>
        </div>
        <div style={{ ...styles.summaryCard, borderLeft: '4px solid #3498db' }}>
          <div style={styles.summaryLabel}>Raw Material</div>
          <div style={{ ...styles.summaryValue, color: '#3498db' }}>{summary.raw_material}</div>
        </div>
        <div style={{ ...styles.summaryCard, borderLeft: '4px solid #f39c12' }}>
          <div style={styles.summaryLabel}>Trading Goods</div>
          <div style={{ ...styles.summaryValue, color: '#f39c12' }}>{summary.trading_goods}</div>
        </div>
        <div style={{ ...styles.summaryCard, borderLeft: '4px solid #e74c3c' }}>
          <div style={styles.summaryLabel}>Total Payables</div>
          <div style={{ ...styles.summaryValue, color: '#e74c3c' }}>
            {formatLakh(summary.total_payables)}
          </div>
        </div>
      </div>

      {/* ─── Filters + Search + Add ──────────────────── */}
      <div style={styles.toolbar}>
        <div style={styles.filterTabs}>
          {[
            { id: 'all', label: 'All' },
            { id: 'raw_material', label: 'Raw Material' },
            { id: 'trading_goods', label: 'Trading Goods' },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setTypeFilter(f.id)}
              style={{
                ...styles.filterButton,
                background: typeFilter === f.id ? '#e74c3c' : 'white',
                color: typeFilter === f.id ? 'white' : '#2c3e50',
                borderColor: typeFilter === f.id ? '#e74c3c' : '#e0e0e0',
              }}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div style={styles.toolbarRight}>
          <div style={styles.searchWrap}>
            <span style={styles.searchIcon}>⌕</span>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, phone, or NTN..."
              style={styles.searchInput}
            />
          </div>
          <button onClick={handleAddClick} style={styles.addButton}>
            + Add vendor
          </button>
        </div>
      </div>

      {/* ─── Vendors Table ───────────────────────────── */}
      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr style={styles.headerRow}>
              <th style={styles.th}>Vendor</th>
              <th style={styles.th}>Type</th>
              <th style={styles.th}>Contact</th>
              <th style={styles.th}>NTN / STRN</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>Payable</th>
              <th style={{ ...styles.th, textAlign: 'center' }}>Terms</th>
              <th style={styles.th}>Status</th>
              <th style={{ ...styles.th, textAlign: 'center' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="8" style={styles.noData}>
                  <div style={{ fontSize: '32px', marginBottom: '8px' }}>⏳</div>
                  Loading vendors...
                </td>
              </tr>
            ) : vendors.length === 0 ? (
              <tr>
                <td colSpan="8" style={styles.noData}>
                  <div style={{ fontSize: '40px', marginBottom: '8px' }}>🏪</div>
                  {debouncedSearch || typeFilter !== 'all'
                    ? 'No vendors match your filters'
                    : 'No vendors yet — click "+ Add vendor" to create one'}
                </td>
              </tr>
            ) : (
              vendors.map((v) => {
                const payable = parseFloat(v.outstanding_balance) || 0;

                return (
                  <tr key={v.id} style={{ ...styles.row, opacity: v.is_active ? 1 : 0.55 }}>
                    <td style={styles.td}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={styles.avatar}>
                          {v.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div style={{ fontWeight: '700', color: '#2c3e50' }}>{v.name}</div>
                          {v.address && (
                            <div style={{ fontSize: '12px', color: '#95a5a6', marginTop: '2px' }}>
                              📍 {truncate(v.address, 40)}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td style={styles.td}>
                      <span style={getTypeBadgeStyle(v.type)}>{formatType(v.type)}</span>
                    </td>
                    <td style={styles.td}>
                      {v.contact_person && (
                        <div style={{ fontSize: '13px', color: '#2c3e50' }}>
                          👤 {v.contact_person}
                        </div>
                      )}
                      {v.phone && (
                        <div style={{ fontSize: '13px', color: '#7f8c8d', marginTop: '2px' }}>
                          ☎ {v.phone}
                        </div>
                      )}
                      {!v.contact_person && !v.phone && '—'}
                    </td>
                    <td style={styles.td}>
                      {v.ntn && (
                        <div style={{ fontSize: '12px', color: '#2c3e50' }}>
                          <strong>NTN:</strong> {v.ntn}
                        </div>
                      )}
                      {v.strn && (
                        <div style={{ fontSize: '12px', color: '#7f8c8d', marginTop: '2px' }}>
                          <strong>STRN:</strong> {v.strn}
                        </div>
                      )}
                      {!v.ntn && !v.strn && '—'}
                    </td>
                    <td style={{ ...styles.td, textAlign: 'right' }}>
                      <div style={{
                        fontWeight: '700',
                        color: payable > 0 ? '#e74c3c' : '#27ae60',
                      }}>
                        Rs {formatNumber(payable)}
                      </div>
                    </td>
                    <td style={{ ...styles.td, textAlign: 'center' }}>
                      <span style={styles.termsBadge}>
                        {v.payment_terms_days != null ? `${v.payment_terms_days} days` : '—'}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <span style={v.is_active ? styles.activeBadge : styles.inactiveBadge}>
                        {v.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={{ ...styles.td, textAlign: 'center' }}>
                      <div style={{ display: 'inline-flex', gap: '6px' }}>
                        <button onClick={() => handleEditClick(v)} style={styles.editButton} title="Edit">
                          ✏️
                        </button>
                        <button onClick={() => handleDelete(v)} style={styles.deleteButton} title="Delete">
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* ─── Pagination ──────────────────────────────── */}
      {!loading && pagination.total > 0 && (
        <div style={styles.paginationWrap}>
          <div style={styles.paginationInfo}>
            Showing {(pagination.page - 1) * pagination.limit + 1}
            –{Math.min(pagination.page * pagination.limit, pagination.total)}
            {' '}of {pagination.total} vendors
          </div>
          <div style={styles.paginationButtons}>
            <button
              onClick={() => setPage(1)}
              disabled={page === 1}
              style={{ ...styles.pageButton, ...(page === 1 ? styles.pageButtonDisabled : {}) }}
            >
              «
            </button>
            <button
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              style={{ ...styles.pageButton, ...(page === 1 ? styles.pageButtonDisabled : {}) }}
            >
              ‹ Prev
            </button>
            <span style={styles.pageIndicator}>
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <button
              onClick={() => setPage(page + 1)}
              disabled={page >= pagination.totalPages}
              style={{ ...styles.pageButton, ...(page >= pagination.totalPages ? styles.pageButtonDisabled : {}) }}
            >
              Next ›
            </button>
            <button
              onClick={() => setPage(pagination.totalPages)}
              disabled={page >= pagination.totalPages}
              style={{ ...styles.pageButton, ...(page >= pagination.totalPages ? styles.pageButtonDisabled : {}) }}
            >
              »
            </button>
          </div>
        </div>
      )}

      {/* ─── Add/Edit Modal ──────────────────────────── */}
      {showFormModal && (
        <VendorFormModal
          vendor={editingVendor}
          onClose={() => { setShowFormModal(false); setEditingVendor(null); }}
          onSaved={handleSaved}
        />
      )}

    </div>
  );
};

// ─── Helpers ──────────────────────────────────────────────

const formatType = (type) => {
  const map = { raw_material: 'Raw Material', trading_goods: 'Trading Goods' };
  return map[type] || type;
};

const getTypeBadgeStyle = (type) => {
  const palette = {
    raw_material:  { bg: '#cce5ff', fg: '#004085' },
    trading_goods: { bg: '#fff3cd', fg: '#856404' },
  };
  const c = palette[type] || { bg: '#e2e3e5', fg: '#383d41' };
  return {
    background: c.bg, color: c.fg,
    padding: '4px 12px', borderRadius: '12px',
    fontSize: '12px', fontWeight: '600',
    display: 'inline-block',
  };
};

const formatNumber = (n) => {
  const num = parseFloat(n) || 0;
  return Math.round(num).toLocaleString('en-PK');
};

const formatLakh = (n) => {
  const num = parseFloat(n) || 0;
  if (num >= 100000) {
    return `Rs ${(num / 100000).toFixed(1)}L`;
  }
  return `Rs ${formatNumber(num)}`;
};

const truncate = (text, max) => {
  if (!text) return '';
  return text.length > max ? text.slice(0, max) + '…' : text;
};

// ─── Styles ───────────────────────────────────────────────

const styles = {
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '16px',
    marginBottom: '24px',
  },
  summaryCard: {
    background: 'white',
    padding: '18px 22px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
  },
  summaryLabel: {
    fontSize: '13px', color: '#7f8c8d',
    marginBottom: '6px', fontWeight: '600',
  },
  summaryValue: {
    fontSize: '28px', fontWeight: '700',
  },

  toolbar: {
    background: 'white',
    padding: '16px 20px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    display: 'flex', flexWrap: 'wrap', gap: '16px',
    justifyContent: 'space-between', alignItems: 'center',
    marginBottom: '16px',
  },
  filterTabs: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  filterButton: {
    padding: '8px 16px',
    border: '2px solid', borderRadius: '8px',
    fontSize: '13px', fontWeight: '600',
    cursor: 'pointer', transition: 'all 0.2s',
  },
  toolbarRight: { display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' },
  searchWrap: { position: 'relative' },
  searchIcon: {
    position: 'absolute', left: '12px', top: '50%',
    transform: 'translateY(-50%)',
    color: '#95a5a6', fontSize: '16px',
  },
  searchInput: {
    padding: '10px 12px 10px 36px',
    border: '2px solid #e0e0e0', borderRadius: '8px',
    fontSize: '14px', width: '280px',
    fontFamily: 'inherit',
  },
  addButton: {
    padding: '10px 20px',
    background: '#e74c3c', color: 'white',
    border: 'none', borderRadius: '8px',
    cursor: 'pointer', fontSize: '14px', fontWeight: '600',
  },

  tableWrap: {
    background: 'white',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    overflow: 'auto',
  },
  table: { width: '100%', borderCollapse: 'collapse' },
  headerRow: { background: '#f8f9fa' },
  th: {
    padding: '14px 16px', textAlign: 'left',
    fontSize: '13px', fontWeight: '700',
    color: '#2c3e50', borderBottom: '2px solid #e0e0e0',
    whiteSpace: 'nowrap',
  },
  row: { borderBottom: '1px solid #f0f0f0' },
  td: { padding: '12px 16px', fontSize: '14px', color: '#2c3e50' },
  noData: {
    padding: '60px 20px', textAlign: 'center',
    color: '#95a5a6', fontSize: '14px',
  },

  avatar: {
    width: '40px', height: '40px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #f39c12 0%, #d35400 100%)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: 'white', fontWeight: '700', fontSize: '16px',
    flexShrink: 0,
  },

  termsBadge: {
    background: '#e3f2fd', color: '#1565c0',
    padding: '4px 10px', borderRadius: '12px',
    fontSize: '12px', fontWeight: '600',
    display: 'inline-block',
  },

  activeBadge: {
    background: '#d4edda', color: '#155724',
    padding: '4px 12px', borderRadius: '12px',
    fontSize: '12px', fontWeight: '600',
  },
  inactiveBadge: {
    background: '#f8d7da', color: '#721c24',
    padding: '4px 12px', borderRadius: '12px',
    fontSize: '12px', fontWeight: '600',
  },

  editButton: {
    padding: '6px 10px',
    background: '#3498db', color: 'white',
    border: 'none', borderRadius: '6px',
    cursor: 'pointer', fontSize: '14px',
  },
  deleteButton: {
    padding: '6px 10px',
    background: '#e74c3c', color: 'white',
    border: 'none', borderRadius: '6px',
    cursor: 'pointer', fontSize: '14px',
  },

  paginationWrap: {
    marginTop: '16px',
    background: 'white',
    padding: '14px 20px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', flexWrap: 'wrap', gap: '12px',
  },
  paginationInfo: { fontSize: '13px', color: '#7f8c8d' },
  paginationButtons: { display: 'flex', gap: '6px', alignItems: 'center' },
  pageButton: {
    padding: '8px 14px',
    background: 'white', color: '#2c3e50',
    border: '2px solid #e0e0e0', borderRadius: '8px',
    cursor: 'pointer', fontSize: '13px', fontWeight: '600',
  },
  pageButtonDisabled: {
    opacity: 0.4, cursor: 'not-allowed',
  },
  pageIndicator: {
    padding: '0 12px', fontSize: '13px',
    color: '#2c3e50', fontWeight: '600',
  },
};

export default VendorsTab;